import achievementsJson from '../balance/achievements.json' with { type: 'json' };
import buildingsJson from '../balance/buildings.json' with { type: 'json' };
import economyJson from '../balance/economy.json' with { type: 'json' };
import eventsJson from '../balance/events.json' with { type: 'json' };
import heroesJson from '../balance/heroes.json' with { type: 'json' };
import itemsJson from '../balance/items.json' with { type: 'json' };
import minesJson from '../balance/mines.json' with { type: 'json' };
import politicsJson from '../balance/politics.json' with { type: 'json' };
import questsJson from '../balance/quests.json' with { type: 'json' };
import racesJson from '../balance/races.json' with { type: 'json' };
import resourcesJson from '../balance/resources.json' with { type: 'json' };
import shipsJson from '../balance/ships.json' with { type: 'json' };
import shopJson from '../balance/shop.json' with { type: 'json' };
import unitsJson from '../balance/units.json' with { type: 'json' };
import worldsJson from '../balance/worlds.json' with { type: 'json' };
// Типизированный доступ к балансным таблицам shared/balance/*.json.
// Сервер загружает их в БД при seed и может горячо перезагрузить (Redis Pub/Sub "balance:reload").
import type { CastleSkinPrices } from './castleSkins.js';
import type {
  BoosterType,
  BuildingType,
  BuildingZone,
  EquipmentSlotType,
  HeroClass,
  HeroStat,
  HeroTalent,
  PartialResources,
  RaceKey,
  Rarity,
  ResourceType,
  ShipType,
  UnitClass,
} from './types.js';

export interface ResourcesBalance {
  types: ResourceType[];
  start: Record<ResourceType, number>;
  /** Запас нового NPC-королевства: щедрый старт игрока не должен стать добычей. */
  npcStart: Record<ResourceType, number>;
  tutorialReward: PartialResources;
  tutorialUnits: Record<string, number>;
  tutorialSpeedups?: Record<string, number>;
  startGems: number;
  offlineCapHours: number;
  storage: { base: number; exp: number; uncapped: ResourceType[]; townHallBase: number };
  /** Защита одного склада от грабежа: perLevel за уровень, с уровня bonusFromLevel — bonusPerLevel за уровень */
  warehouseProtection: { perLevel: number; bonusFromLevel: number; bonusPerLevel: number };
  plunderShare: number;
  goldExchange: {
    unitsPerGem: Record<ResourceType, number>;
    exchangeFeePct: number;
  };
  starvationCombatPenaltyPct: number;
  foodUpkeepAllowedNegative: boolean;
}

export interface BuildingDef {
  nameRu: string;
  nameEn: string;
  zone: BuildingZone;
  /** Сколько зданий этого типа можно поставить. */
  maxCount: number;
  /** Индивидуальный предел уровня; если не задан, используется общий предел. */
  maxLevel?: number;
  timeFactor: number;
  requiresTownHall: number;
  startLevel: number;
  baseCost: PartialResources;
  produces: PartialResources;
  effects: Record<string, number | boolean | string>;
}

/** Исследование академии: эффект на уровень. */
export interface ResearchDef {
  nameRu: string;
  nameEn: string;
  effect:
    | 'productionPct'
    | 'buildSpeedPct'
    | 'trainSpeedPct'
    | 'marchSpeedPct'
    | 'carryPct'
    | 'woundedPct'
    | 'protectPct'
    | 'scoutSilencePct'
    | 'scoutDeceitPct'
    | 'scoutDefusePct'
    | 'scoutSearchPct'
    /** Воздушный шар: уровень = открытые слои подсветки на мини-карте (shared/src/balloon.ts). */
    | 'mapScan';
  perLevel: number;
  /** Где исследуют (по умолчанию — академия); уровень не выше уровня этого здания. */
  building?: 'academy' | 'headquarters';
  baseCost: PartialResources;
}

export interface ResearchBalance {
  maxLevel: number;
  costExponent: number;
  baseTimeSec: number;
  timeExponent: number;
  maxTimeSec: number;
  list: Record<string, ResearchDef>;
}

export interface BuildingsBalance {
  maxLevel: number;
  costExponent: number;
  productionExponent: number;
  timeTableSec: Record<string, number>;
  freeSpeedupSec: number;
  gemsPerMinuteSpeedup: number;
  /** Строителей в каждом замке (одновременных строек и улучшений); VIP — больше. */
  builders: number;
  /** Цена (золото) наёмного строителя: стройка сверх лимита, когда все строители замка заняты. */
  extraBuilderGems: number;
  /** Число участков: castle — в замке, field — на поле ресурсов. */
  plots: { castle: number; field: number };
  /** Удорожание каждого следующего здания того же типа (доля от базовой цены). */
  instanceCostStep: { castle: number; field: number };
  /** Уровни, на которых меняется вид здания. */
  artStages: number[];
  /** Основные здания замка: ратушу (уровень замка) нельзя поднять выше, пока каждое не дошло до её уровня. */
  /** Какие здания дотянуть до текущей ратуши, чтобы поднять её до уровня-ключа. */
  townHallSteps?: Record<string, BuildingType[]>;
  list: Record<BuildingType, BuildingDef>;
  /** Исследования академии. */
  research: ResearchBalance;
}

export interface UnitDef {
  nameRu: string;
  nameEn: string;
  class: UnitClass;
  damageType: 'physical' | 'siege' | 'magic';
  attack: number;
  defense: number;
  hp: number;
  speed: number;
  carry: number;
  upkeep: number;
  wallMultiplier?: number;
  cost: PartialResources;
  trainTimeSec: number;
  barracksLevel: number;
  /** Элитный тир открывается только на максимальном уровне своего здания найма. */
  requiresMaxBuildingLevel?: boolean;
  costGems: number;
}

export interface ScoutingBalance {
  /** Доля погибших разведчиков атакующего при успехе: share × (защита / атака). */
  attackerLossOnSuccessShare: number;
  /** % потерь разведчиков защитника при успешной вражеской разведке. */
  defenderLossOnSuccessPct: number;
  /** Доля погибших разведчиков защитника при отбитой разведке: share × (атака / защита). */
  defenderLossOnFailShare: number;
  /** Во сколько раз нужно превосходить защиту для точного (без погрешности) отчёта. */
  accurateRatio: number;
  /** Диверсии после успешной разведки замка. */
  sabotage: SabotageBalance;
  /** Вражеская разведка не убивает всех разведчиков замка: в живых остаётся не меньше defenderSurvivePct % (и не меньше одного). */
  defenderSurvivePct: number;
  /** Провал разведки: в плен попадает от minPct до maxPct % отправленных разведчиков (не меньше одного). */
  capturedOnFail: { minPct: number; maxPct: number };
  /** Допрос: ложь искажает число в lieMin…lieMax раз (в меньшую или большую сторону). */
  interrogation: { lieMinFactor: number; lieMaxFactor: number };
}

export interface SabotageBalance {
  /** Отравить воду: умирает до killPct % гарнизона (не больше killPerScout на разведчика), до woundPct % — в лазарет (если там есть места). */
  poisonWater: { killPct: number; woundPct: number; killPerScout: number; woundPerScout: number };
  /** Отравить еду: портится до foodPct % еды замка, не больше foodPerScout на разведчика. */
  poisonFood: { foodPct: number; foodPerScout: number };
  /** Взять в плен: scoutsPerPrisoner разведчиков уровня N уводят одного воина уровня N. */
  capture: { scoutsPerPrisoner: number };
  /**
   * Бомба: каждые scoutsPerLevel выживших разведчиков — минус уровень здания (не больше maxLevels), взрыв через
   * timerSec. Защитник ищет её разведчиками замка: здания обыскивают по порядку searchOrder (важные — первыми),
   * каждые searchScoutsPerBuilding разведчиков — одно здание за searchSecPerBuilding (отряды параллельно);
   * или находит сразу за findGems золота. Найденную разминирует, перерезав настоящий фитиль.
   */
  bomb: {
    scoutsPerLevel: number;
    maxLevels: number;
    timerSec: number;
    searchScoutsPerBuilding: number;
    searchSecPerBuilding: number;
    /** Меньше searchScoutsPerBuilding разведчиков — один отряд, медленнее: × (порог / разведчиков)^searchSmallExp. */
    searchSmallExp: number;
    searchOrder: string[];
    findGems: number;
  };
}

export interface UnitUpgradesBalance {
  maxLevel: number;
  attackPctPerLevel: number;
  defensePctPerLevel: number;
  costMultiplier: number;
  costExponent: number;
  baseTimeSec: number;
  timeExponent: number;
  maxTimeSec: number;
  /** Здание, где улучшают войска (уровень улучшения не выше уровня здания). */
  building?: BuildingType;
}

export interface UnitsBalance {
  maxTrainBatch: number;
  counters: Record<UnitClass, UnitClass | 'wall'>;
  counterMultiplier: number;
  initiative: Record<UnitClass | 'hero', number>;
  scouting: ScoutingBalance;
  upgrades: UnitUpgradesBalance;
  /** Где нанимается класс войск (по умолчанию — казарма): кавалерия — конюшня, осадные — кузница, разведчики — штаб. */
  classBuilding?: Partial<Record<UnitClass, BuildingType>>;
  list: Record<string, UnitDef>;
  npc: Record<string, UnitDef>;
}

export interface RaceDef {
  nameRu: string;
  nameEn: string;
  beats: RaceKey;
  bonuses: Record<string, number>;
  /** Ресурс, который составляет основную долю материальной цены войск этой расы. */
  keyResource: ResourceType;
  /** Названия войск расы: ключ юнита → [ru, en]. */
  unitNames?: Record<string, [string, string]>;
}

export interface RacesBalance {
  list: Record<RaceKey, RaceDef>;
  matchup: { attackPct: number; damageTakenPct: number };
  allianceDiversityBonusPct: number;
}

export type SkillEffect =
  | { kind: 'buff'; stat: string; value: number; rounds: number }
  | { kind: 'debuff'; stat: string; value: number; rounds: number }
  | { kind: 'damage'; base: number; perStat: number; stat: HeroStat; ignoresDefense: boolean }
  | { kind: 'damage_all'; base: number; perStat: number; stat: HeroStat; ignoresDefense: boolean }
  | { kind: 'passive'; stat: string; value: number };

export interface SkillDef {
  class: HeroClass;
  type: 'active' | 'passive';
  cooldown: number;
  nameRu: string;
  nameEn: string;
  descriptionRu: string;
  descriptionEn: string;
  effect: SkillEffect;
}

export interface HeroClassDef {
  nameRu: string;
  nameEn: string;
  bonuses: Record<string, number | boolean>;
  startBonus: Partial<Record<HeroStat, number>>;
}

export interface HeroesBalance {
  maxLevel: number;
  xpBase: number;
  xpExponent: number;
  pointsPerLevel: number;
  maxSkillSlots: number;
  startStats: Record<HeroStat, number>;
  statEffects: Record<HeroStat, Record<string, number>>;
  baseCritPct: number;
  critMultiplier: number;
  maxEvasionPct: number;
  woundedMinutes: number;
  extraHeroCostGold: number;
  maxHeroes: number;
  /** Неосновной замок: с какого уровня его Храма героев у замка свой герой и сколько их. */
  castleHeroTempleLevel?: number;
  castleHeroes?: number;
  dungeon: { dailyRuns: number; xpPerLevel: number; itemDropPct: number };
  xpSources: { battleWin: number; perKillHp: number; quest: number; trade: number };
  classes: Record<HeroClass, HeroClassDef>;
  skills: Record<string, SkillDef>;
  /** Навыки героя: какой бонус даёт очко, сколько, и сколько очков можно вложить в один навык. */
  talents: Record<HeroTalent, { bonus: string; perPoint: number; maxPoints: number }>;
  /** Сброс распределённых очков навыков (все очки возвращаются) — цена в золоте. */
  talentResetGems: number;
  /** Лечение раненого героя за золото: цена за минуту оставшегося лечения. */
  healGoldPerMinute?: number;
}

/** Направление снаряжения: на что герой усиливает армию. */
export type ItemLine = 'assault' | 'bulwark' | 'balance' | 'wanderer';

export interface ItemDef {
  slot: EquipmentSlotType;
  /** Направление (комплект); у старых сохранённых предметов может отсутствовать. */
  line?: ItemLine;
  rarity: Rarity;
  nameRu: string;
  nameEn: string;
  levelReq: number;
  bonuses: Record<string, number>;
  iconKey: string;
}

export interface ItemsBalance {
  slots: EquipmentSlotType[];
  rarities: Rarity[];
  dropWeights: Record<Rarity, number>;
  /** Направления снаряжения: название, цвет и подсказка. */
  lines: Record<ItemLine, { nameRu: string; nameEn: string; color: string; hintRu: string; hintEn: string }>;
  /** Бонус комплекта: число вещей одного направления → бонусы. */
  sets: Record<ItemLine, Record<string, Record<string, number>>>;
  /** Осколки: цена ковки вещи по редкости и переплавка в следующую редкость. */
  fragments: { craftCost: Record<Rarity, number>; mergeCost: number };
  list: Record<string, ItemDef>;
}

export interface ShipDef {
  nameRu: string;
  nameEn: string;
  attack: number;
  defense: number;
  hp: number;
  speed: number;
  cargo: number;
  /** Сколько сухопутных воинов корабль берёт на борт (морской десант). */
  troops?: number;
  cost: PartialResources;
  buildTimeSec: number;
  shipyardLevel: number;
}

export interface ShipsBalance {
  maxShipsPerFleet: number;
  /** Размер флота: base кораблей + 1 за каждые perPortLevels уровней здания «Порт», не больше max. */
  fleetSize: { base: number; perPortLevels: number; max: number };
  flagshipBonusPct: number;
  windRangePct: number;
  maxRounds: number;
  repairGoldPerHp: number;
  list: Record<ShipType, ShipDef>;
  npc: Record<string, ShipDef>;
  captain: {
    baseHireGold: number;
    perLevelGold: number;
    maxLevel: number;
    skills: string[];
    effects: Record<string, Record<string, number>>;
  };
  goods: Record<string, { nameRu: string; nameEn: string; basePrice: number }>;
  market: {
    volatilityPct: number;
    meanReversion: number;
    minFactor: number;
    maxFactor: number;
    feePct: number;
    eventChancePct: number;
  };
  ports: {
    perTiles: number;
    neutralPct: number;
    abandonedPct: number;
    piratePct: number;
    buildRadius: number;
    routeMaxDistance: number;
    /** Цена постройки гавани. */
    buildCost: PartialResources;
  };
  treasure: {
    fragmentsNeeded: number;
    fragmentDropPct: number;
    rewardGold: number;
    rewardGems: number;
    rewardSilver: number;
  };
  blockade: { hours: number; tollPct: number };
  /** Морские монстры: змеи (обычные, по всему морю) и кракен (босс мира, бьют всем миром). */
  monsters: Record<'serpent' | 'kraken', SeaMonsterDef>;
  /** Экспедиционный корабль: плавания за обрывками карты (мини-игры) и поиск клада. */
  expedition: {
    buildCost: PartialResources;
    exploreMinutes: number;
    piecesNeeded: number;
    speed: number;
    radiusMin: number;
    radiusMax: number;
    voyageLoot: PartialResources;
    treasure: { resources: PartialResources; fragments: number; gemsMin: number; gemsMax: number };
    /** Награда растёт на столько % за уровень главного замка. */
    levelScalePct: number;
    pairsMaxMoves: number;
    compassLength: number;
  };
  /** Шторма: дрейфующие по морю области, корабли через них идут медленнее. */
  storms: {
    perTiles: number;
    radiusMin: number;
    radiusMax: number;
    speedMin: number;
    speedMax: number;
    lifetimeHoursMin: number;
    lifetimeHoursMax: number;
    slowPct: number;
    /** Шанс, что шторм потопит часть отряда, %; навык героя «Командование в шторм» снижает его. */
    wreckPct: number;
    /** Сколько груза (ресурсов в трюмах) смывает шторм на пути, % — случайно в этих пределах. */
    cargoLossMinPct?: number;
    cargoLossMaxPct?: number;
    /** Сколько кораблей тонет при крушении: от 1 до этого % отряда (округление вверх). */
    wreckMaxPct?: number;
  };
  /** Пираты: плавают по морю и нападают на проходящие рядом флоты игроков, грабят трюмы. */
  pirates?: {
    perTiles: number;
    levelMin: number;
    levelMax: number;
    /** Состав: шлюпов за уровень и фрегатов на каждые frigateEvery уровней. */
    sloopsPerLevel: number;
    frigateEvery: number;
    speedMin: number;
    speedMax: number;
    routeMin: number;
    routeMax: number;
    lifetimeHoursMin: number;
    lifetimeHoursMax: number;
    /** На каком расстоянии (клеток) пираты замечают флот и шанс нападения, %. */
    huntRadius: number;
    attackPct: number;
    /** Сколько % ресурсов в трюмах забирают пираты, если флот не отбился. */
    plunderPct: number;
    /** Награда за каждый потопленный пиратский корабль (× уровень пиратов). */
    rewardsPerShip: Record<string, number>;
    /**
     * Победа пиратов: захватывают до capturePct% уцелевших кораблей флота (из потопленного — до
     * capturePct% погибших), растут на уровень (не выше maxLevel), и задерживаются в море на
     * winExtendHours (всего не дольше maxLifetimeHours). Флот пиратов — не больше maxShips кораблей.
     */
    capturePct?: number;
    maxLevel?: number;
    maxShips?: number;
    winExtendHours?: number;
    maxLifetimeHours?: number;
  };
  bounty: { minGold: number };
  /** Рыбалка: косяки в открытой воде, улов — еда. */
  fishing: {
    perTiles: number;
    levelMin: number;
    levelMax: number;
    /** Запас косяка */
    amount: number;
    /** Лов одного корабля в час */
    ratePerShipPerHour: number;
    /** Как часто улов уходит в замок */
    deliverSec: number;
    /** Шанс налёта пиратов за час стоянки, % */
    pirateRaidPctPerHour: number;
    /** Сколько часов рыбачит NPC */
    npcMaxHours: number;
    respawnMinutes: number;
  };
  /** Морской десант: высадка у берега рядом с целью. */
  /** Дозор: варианты длительности (ч) и припасы команды на час (списываются при отправке). */
  patrol: { hours: number[]; suppliesPerHour: PartialResources };
  landing: { maxCoastDistance: number; maxWaitHours: number; harborSearchRadius: number };
  /** Улучшения флота (в Верфи): уровень не выше уровня Верфи; цена и время растут с уровнем. */
  upgrades: {
    maxLevel: number;
    baseCost: PartialResources;
    costExponent: number;
    baseTimeSec: number;
    timeExponent: number;
    maxTimeSec: number;
    /** Эффект за уровень: speed/attack/defense — %, patrol — клеток радиуса дозора, troops — мест для десанта. */
    stats: Record<'speed' | 'attack' | 'defense' | 'patrol' | 'troops', { perLevel: number }>;
  };
}

export interface QuestRequirement {
  event: string;
  target: number;
  filter?: Record<string, string | number>;
}

/** Что ускоряется: у каждого вида свой бесплатный остаток. */
export type SpeedupKind = 'building' | 'upgrade' | 'research' | 'training' | 'march';

export interface Rewards extends PartialResources {
  gems?: number;
  /** Бусты ускорения: код → количество (speedup_5m, speedup_1h, …). */
  speedups?: Record<string, number>;
  /** Бусты армии в запас: код → количество (march_boost_25, …). */
  marchBoosts?: Record<string, number>;
  /** Бусты щита в запас: код → количество (shield_boost_4h, …). */
  shields?: Record<string, number>;
  /** Сразу включить (продлить) щит на всех замках на столько часов. */
  shieldHours?: number;
  /** Перемещения замка в запас (предмет RELOCATE_ITEM). */
  relocations?: number;
  /** Перелёты на остров без мостов в запас (RELOCATE_ISLAND_ITEM). */
  islandRelocations?: number;
  heroXp?: number;
  bpXp?: number;
  units?: Record<string, number>;
  boosters?: BoosterType[];
  items?: string[];
}

export interface QuestDef {
  key: string;
  type: 'tutorial' | 'story' | 'daily' | 'weekly' | 'alliance' | 'hero' | 'political';
  order?: number;
  titleRu: string;
  titleEn: string;
  descriptionRu: string;
  descriptionEn: string;
  requirements: QuestRequirement;
  rewards: Rewards;
  minLevel: number;
}

export interface CampaignMission {
  mission: number;
  nameRu: string;
  nameEn: string;
  storyRu: string;
  storyEn: string;
  garrison: Record<string, number>;
  wallLevel: number;
  rewards: Rewards;
  requiresMission: number;
}

export interface QuestsBalance {
  dailyCount: number;
  weeklyCount: number;
  resetHourUtc: number;
  list: QuestDef[];
  campaign: CampaignMission[];
}

/** Лагеря NPC (партизаны, налётчики, нечисть, отступники): осколки снаряжения своего направления. */
export interface CampsBalance {
  /** Лагеря взамен разбитых: доля, что ставится рядом с активными игроками (остальные — по всей карте). */
  anchorShare?: number;
  /** Радиус вокруг замка игрока и сколько дней назад он был в игре, чтобы считаться активным. */
  anchorRadius?: number;
  anchorActiveDays?: number;
  perTiles: number;
  levelMin: number;
  levelMax: number;
  /** Во сколько раз гарнизон больше garrisonPerLevel на уровнях 1, 2, 3… (подобрано симуляцией боёв). */
  garrisonByLevel: number[];
  rewardsPerLevel: PartialResources;
  fragmentChancePct: number;
  fragmentChancePerLevelPct: number;
  fragmentChanceMaxPct: number;
  /** Каждые N уровней лагеря — ещё один осколок. */
  fragmentsPerLevelStep: number;
  rarityByLevel: { minLevel: number; weights: Record<Rarity, number> }[];
  kinds: Record<
    string,
    {
      nameRu: string;
      nameEn: string;
      badgeUnit: string;
      line: ItemLine;
      /** Поправка силы типа лагеря с 4-го уровня: на низких все типы одинаково слабые. */
      strength?: number;
      garrisonPerLevel: Record<string, number>;
    }
  >;
}

export interface SeaMonsterDef {
  nameRu: string;
  nameEn: string;
  /** Сколько клеток моря на одного монстра (змеи) или сколько на мир (кракен). */
  perTiles?: number;
  perWorld?: number;
  level?: number;
  levelMin?: number;
  levelMax?: number;
  /** Состав «флота» монстра: база + прибавка за уровень (NPC-корабли из ships.npc). */
  fleetBase: Record<string, number>;
  fleetPerLevel: Record<string, number>;
  /** Награда за каждую убитую единицу монстра (× уровень). */
  rewardsPerUnit: PartialResources;
  /** Тому, кто добил: осколки снаряжения и золото. */
  killFragments: number;
  killGems: number;
  lifetimeHours: number;
  respawnHours?: number;
}

export interface EventsBalance {
  camps: CampsBalance;
  barbarians: {
    nameRu: string;
    nameEn: string;
    intervalHours: number;
    durationHours: number;
    campsPerPlayers: number;
    minCamps: number;
    garrisonPerLevel: Record<string, number>;
    levelMin: number;
    levelMax: number;
    rewardsPerLevel: PartialResources;
    gemsChancePct: number;
  };
  pirates: {
    nameRu: string;
    nameEn: string;
    intervalHours: number;
    durationHours: number;
    havensPerPlayers: number;
    minHavens: number;
    fleetPerLevel: Record<string, number>;
    levelMin: number;
    levelMax: number;
    rewardsPerLevel: PartialResources;
  };
  dragon: {
    nameRu: string;
    nameEn: string;
    intervalHours: number;
    durationHours: number;
    level: number;
    hp: number;
    attack: number;
    defense: number;
    rewardPerDamage: PartialResources;
    killRewards: Rewards;
    topRewards: Rewards;
  };
  siege: {
    nameRu: string;
    nameEn: string;
    intervalHours: number;
    durationHours: number;
    level: number;
    garrison: Record<string, number>;
    wallLevel: number;
    rewards: Rewards;
    influence: number;
  };
  /** Календарь сменяющихся событий с рейтингом (по кругу). */
  rotation: {
    epoch: string;
    lengthHours: number;
    list: { key: string; points: Record<string, number> }[];
    rewards: { from: number; to: number; gems: number }[];
  };
  /** NPC-королевства: развитие обычными действиями игрока в пределах суточного расписания. */
  kingdoms: {
    count: number;
    levelMin: number;
    levelMax: number;
    /** Совместимость со старым балансом; автоматический рост больше не используется. */
    growthHours: number;
    /** Добыча королевства против заброшенного замка того же уровня. */
    lootMultiplier: number;
    /** Шаг по местам заселения: королевство на каждом N-м свободном месте (остальные — игрокам). */
    slotStep: number;
    dailyActiveHours: number;
    attacksPerDayMin: number;
    attacksPerDayMax: number;
    readyCastleLevel: number;
    readyArmyCount: number;
    attackArmyShare: number;
    trainBatch: number;
    allianceGroupSize: number;
    allianceMaxCount: number;
    /** Доля армии на замок уровня ≥ N: [[10, 0.6], [15, 0.75], [20, 0.9]]. */
    attackShareByLevel?: [number, number][];
    /** Сколько армии NPC готов вывести, если меньшей доли для победы не хватает. */
    attackShareMax?: number;
    /** Осады оборонительных башен в сутки и радиус поиска башен от замка. */
    towerAttacksPerDay?: number;
    towerSearchRadius?: number;
    /** Совместный штурм альянса NPC на замки от этого уровня; доля армии союзников в штурме. */
    rallyMinTargetLevel?: number;
    rallyJoinShare?: number;
    /** Предел уровня замка NPC (рост ратуши). */
    maxCastleLevel?: number;
    /** Экономика: серебро на уровень замка, сколько оставить при обмене на рынке, доля лишнего за ход. */
    silverTargetPerLevel?: number;
    marketKeepPerLevel?: number;
    marketSharePerTick?: number;
    /** Крестьяне на уровень замка, отрядов в шахтах и радиус поиска шахты. */
    peasantsPerLevel?: number;
    peasantSquads?: number;
    mineSearchRadius?: number;
    /** На сколько уровней поля могут обгонять замок. */
    fieldLevelsAhead?: number;
    /** Сколько армии главного замка держать гарнизоном в захваченном. */
    extraGarrisonShare?: number;
    /** Пока есть кому мстить, обычная атака только с таким запасом выживших (%). */
    saveArmySurvivorsPct?: number;
    /** Столица мира: альянсы NPC штурмуют её волной атак и подкрепляют, когда держат (npcCapital.service). */
    capitalEnabled?: boolean;
    /** Не чаще одной волны на альянс за столько часов. */
    capitalWaveCooldownHours?: number;
    /** Доля войск каждого участника волны и сколько королевств в волне. */
    capitalAttackShare?: number;
    capitalMaxWave?: number;
    /** Запас по прогнозу боя: волна должна побеждать, даже если её сила поделена на это число. */
    capitalAssaultMargin?: number;
    /** Удержание: подкреплять, пока гарнизон слабее исходного гарнизона столицы × holdFactor. */
    capitalReinforceShare?: number;
    capitalHoldFactor?: number;
  };
  abandoned: {
    perTiles: number;
    levelMin: number;
    levelMax: number;
    respawnMinutes: number;
    inactiveDaysToAbandon: number;
    /** Гарнизон NPC-портов (пиратов) по уровню. */
    garrisonPerLevel: Record<string, number>;
    garrisonExponent: number;
    /** Гарнизон заброшенного замка на уровне levelMin; дальше × garrisonGrowth за каждый уровень. */
    garrisonBase: Record<string, number>;
    garrisonGrowth: number;
    /** С этого уровня в гарнизоне войска 3-го ранга (+ garrisonElitePerLevel за уровень). */
    eliteFromLevel: number;
    garrisonElitePerLevel: Record<string, number>;
    /** Стена: wallBase + уровень замка, не выше wallMax. */
    wallBase: number;
    wallMax: number;
    /** Доля уже построенных зданий (от, до) — достаётся захватчику. */
    buildingsShare: [number, number];
    lootPerLevel: PartialResources;
  };
  keypoints: { level: number; garrison: Record<string, number>; wallLevel: number };
  oases: {
    count: number;
    tiers: { reductionPct: number; level: number; garrison: Record<string, number> }[];
  };
  capital: { garrison: Record<string, number>; wallLevel: number };
}

export interface VipLevelDef {
  productionPct: number;
  builders: number;
  offlineHours: number;
  buildTimePct: number;
  freeSpeedupSec: number;
}

export interface EconomyBalance {
  targets: { goldDay1: number; goldDay7: number; goldDay30: number };
  sources: string[];
  sinks: string[];
  luxuryTax: { thresholdGold: number; ratePctPerDay: number };
  march: {
    /** Минимум специальной разведывательной миссии; обычные походы не ограничивает. */
    minSeconds: number;
    /** Устаревшая настройка для совместимости сохранённого баланса; походы не ограничивает. */
    maxSeconds: number;
    secondsPerTile: number;
    maxDistance: number;
    maxMarchesBase: number;
    /** Предел одновременных походов на максимальном уровне замка. */
    maxMarchesCap?: number;
    scoutSeconds: number;
    newbieShieldHours: number;
    maxHeroOnMarch: number;
  };
  /**
   * Бесплатное ускорение последних секунд таймера: freeUnderSec — по умолчанию,
   * freeByKind — по видам (стройки, улучшения, исследования, найм, походы); 0 — бесплатно нельзя.
   */
  speedup: { gemsPerMinute: number; freeUnderSec: number; freeByKind?: Partial<Record<SpeedupKind, number>> };
  /** Бусты ускорения: минуты и цена в золоте. Бесплатно — только последние freeUnderSec секунд. */
  speedupItems: Record<string, { minutes: number; priceGems: number; nameRu: string; nameEn: string }>;
  /** Бусты армии (марш-бросок): войска в пути идут быстрее на pct% до конца пути. */
  marchBoostItems: Record<string, { pct: number; priceGems: number; nameRu: string; nameEn: string }>;
  /** Бусты щита: включают (продлевают) щит на всех замках игрока на hours часов. */
  /** Обычный щит закрывает один замок; allCastles — сразу все замки игрока. */
  shieldItems: Record<
    string,
    { hours: number; priceGems: number; nameRu: string; nameEn: string; allCastles?: boolean }
  >;
  dailyRewards: Rewards[];
  vip: Record<string, VipLevelDef>;
  boosters: Record<BoosterType, { multiplier: number; hours: number }>;
  /** Переселение замка в другую клетку мира: цена в кристаллах и перерыв между переездами.
   *  Предмет RELOCATE_ITEM из запаса заменяет оплату (перерыв всё равно действует). */
  /** islandGems — перелёт на остров без мостов (туда только морем): дороже, обычное перемещение не действует. */
  relocate: { gems: number; cooldownHours: number; islandGems?: number };
  /** Смена главного замка: цена в золоте и пауза между сменами. */
  mainCastleChange?: { gems: number; cooldownHours: number };
  /** Переименование героя, золото. */
  heroRenameGems?: number;
  referral: {
    referrerGems: number;
    referredGems: number;
    requiredCastleLevel: number;
    /** Вторая награда: друг дорос до bonusCastleLevel. */
    bonusCastleLevel: number;
    bonusReferrerGems: number;
    bonusReferredGems: number;
    /** Подарок приглашённому сразу при регистрации по ссылке. */
    welcomeGems: number;
  };
  battlePass: { levels: number; xpPerLevel: number; premiumGems: number };
  alliance: { createCostGold: number; maxMembers: number; donateMin: number };
  autoban: { actionsPerSecond: number; banHours: number };
  chat: { perSecond: number; perMinute: number; duplicateBlockSec: number; maxLength: number };
  pushPerDay: number;
}

export interface PoliticsBalance {
  influence: {
    captureCastlePerLevel: number;
    capturePortPerLevel: number;
    pvpWin: number;
    capitalHourly: number;
    keyPointHourly: number;
    holdCastle7Days: number;
    loseCastlePerLevel: number;
    warVictoryPct: number;
    protestPenaltyPct: number;
  };
  tax: {
    min: number;
    max: number;
    default: number;
    changeCooldownHours: number;
    tyrannyThreshold: number;
    rebellionBonusPct: number;
  };
  /**
   * «Битва за Место силы»: раз в неделю окно, когда Место силы (столицу мира) можно штурмовать.
   * weekday — день недели UTC (0 — воскресенье), hourUtc — начало окна; держатель к концу окна побеждает.
   */
  powerBattle: {
    weekday: number;
    hourUtc: number;
    durationHours: number;
    winnerInfluence: number;
    rewardGemsPerMember: number;
  };
  ruling: {
    challengeMarginPct: number;
    challengeHours: number;
    capitalHoldHours: number;
    seasonEndInfluenceKeepPct: number;
  };
  capital: {
    resourceBonusPct: number;
    radiusProtected: number;
    /** Подкрепления в свою столицу быстрее на столько процентов. */
    reinforceSpeedPct?: number;
    /** Сколько ресурсов приносит столица в казну альянса-владельца каждый час. */
    treasuryHourly?: Record<string, number>;
    /** Сколько процентов прочности стены чинится за час. */
    wallRepairHourlyPct?: number;
    /** Очков удержания за час и золото на участника за 1, 2, 3 места сезона. */
    pointsHourly?: number;
    seasonRewards?: number[];
    /** Улучшения из казны альянса; цена — базовая × уровень. */
    upgrades?: {
      walls: { maxLevel: number; wallLevelsPerLevel: number; cost: Record<string, number> };
      garrison: { maxLevel: number; defensePctPerLevel: number; cost: Record<string, number> };
    };
  };
  keyPoints: {
    count: number;
    radiusShare: number;
    /** Вылазки из крепости альянса, который держит и Место силы, быстрее на столько процентов. */
    capitalLinkSpeedPct?: number;
    /** Улучшения крепости из казны альянса-владельца; цена — базовая × уровень. */
    upgrades?: {
      walls: { maxLevel: number; wallLevelsPerLevel: number; cost: Record<string, number> };
      garrison: { maxLevel: number; defensePctPerLevel: number; cost: Record<string, number> };
      roads: { maxLevel: number; speedPctPerLevel: number; cost: Record<string, number> };
    };
  };
  coalition: {
    durationDays: number;
    minAlliances: number;
    minMembersPerAlliance: number;
    maxAlliances: number;
  };
  protest: { cooldownHours: number; thresholdShare: number; windowHours: number };
  treasury: { proposalHours: number; quorumShare: number };
  titles: Record<string, { nameRu: string; nameEn: string }>;
}

export interface WorldsBalance {
  /** Главный замок появляется не дальше стольких клеток от моря (гавань доступна всем). */
  maxSeaDistance: number;
  defaultMaxPlayers: number;
  sizes: Record<string, number>;
  regionsPerSide: number;
  castleTilesPerPlayer: number;
  spawnRing: { min: number; max: number };
  minCastleDistance: number;
  honeypots: number;
  seasonDays: number;
  migration: {
    freeAfterDays: number;
    paidAfterDays: number;
    paidGems: number;
    paidCooldownDays: number;
    cooldownDays: number;
    maxCastleLevel: number;
    castleLevelPenalty: number;
    resourcesKeepPct: number;
    armyKeepPct: number;
    heroXpLossPct: number;
  };
  autoCreateWhenFullPct: number;
  mergeBelowPlayers: number;
  snapshot: { everyMinutes: number; retentionHours: number };
  gameEventRetentionDays: number;
}

export interface ShopItemDef {
  code: string;
  type: string;
  nameRu: string;
  nameEn: string;
  priceGems: number;
  priceGold?: number;
  priceSilver?: number;
  priceReal?: Record<string, number>;
  rewards: Rewards & {
    vip?: number;
    days?: number;
    battlePass?: boolean;
    skin?: string;
    migration?: boolean;
    raceChange?: boolean;
  };
  iconKey: string;
  oncePerUser?: boolean;
}

export interface ShopBalance {
  castleSkins: CastleSkinPrices;
  items: ShopItemDef[];
  dailyOffers: { count: number; discountPct: number; pool: string[] };
}

export interface AchievementDef {
  code: string;
  titleRu: string;
  titleEn: string;
  descriptionRu: string;
  descriptionEn: string;
  stat: string;
  target: number;
  rewards: Rewards;
}

export interface AchievementsBalance {
  list: AchievementDef[];
}

export interface MinesBalance {
  perTiles: number;
  minDistanceFromCapital: number;
  levelMin: number;
  levelMax: number;
  levels: Record<string, { amount: number; ratePerHour: number }>;
  resourceFactor: Record<ResourceType, number>;
  terrainWeights: Record<string, Partial<Record<ResourceType, number>>>;
  names: Record<ResourceType, [string, string]>;
  minGatherSeconds: number;
  maxGatherMinutes: number;
  /** Крестьяне в шахте: каждый добывает peasantPerHour × коэффициент ресурса в час, но вместе — не больше скорости шахты × peasantRateMultiplier; добытое сразу идёт в замок раз в peasantDeliverSec. */
  peasantRateMultiplier: number;
  peasantPerHour: number;
  peasantDeliverSec: number;
  /** Шахту отбили: доля крестьян отряда (от min до max %), уводимых победителем в плен. */
  peasantCapturePct: { min: number; max: number };
}

export interface Balance {
  resources: ResourcesBalance;
  buildings: BuildingsBalance;
  units: UnitsBalance;
  races: RacesBalance;
  heroes: HeroesBalance;
  items: ItemsBalance;
  ships: ShipsBalance;
  quests: QuestsBalance;
  events: EventsBalance;
  economy: EconomyBalance;
  politics: PoliticsBalance;
  worlds: WorldsBalance;
  shop: ShopBalance;
  achievements: AchievementsBalance;
  mines: MinesBalance;
}

export const BALANCE_KEYS = [
  'resources',
  'buildings',
  'units',
  'races',
  'heroes',
  'items',
  'ships',
  'quests',
  'events',
  'economy',
  'politics',
  'worlds',
  'shop',
  'achievements',
  'mines',
] as const satisfies readonly (keyof Balance)[];
export type BalanceKey = (typeof BALANCE_KEYS)[number];

/** Баланс по умолчанию — из JSON-файлов репозитория. */
export const defaultBalance: Balance = {
  resources: resourcesJson as unknown as ResourcesBalance,
  buildings: buildingsJson as unknown as BuildingsBalance,
  units: unitsJson as unknown as UnitsBalance,
  races: racesJson as unknown as RacesBalance,
  heroes: heroesJson as unknown as HeroesBalance,
  items: itemsJson as unknown as ItemsBalance,
  ships: shipsJson as unknown as ShipsBalance,
  quests: questsJson as unknown as QuestsBalance,
  events: eventsJson as unknown as EventsBalance,
  economy: economyJson as unknown as EconomyBalance,
  politics: politicsJson as unknown as PoliticsBalance,
  worlds: worldsJson as unknown as WorldsBalance,
  shop: shopJson as unknown as ShopBalance,
  achievements: achievementsJson as unknown as AchievementsBalance,
  mines: minesJson as unknown as MinesBalance,
};

/** Все юниты (игровые + NPC) одним словарём — нужен боевому движку. */
export function allUnitDefs(b: Balance): Record<string, UnitDef> {
  return { ...b.units.npc, ...b.units.list };
}

/** Название юнита с учётом расы (у каждой расы свои названия войск). */
export function unitName(
  b: Balance,
  unitKey: string,
  race: RaceKey | string | null | undefined,
  lang: 'ru' | 'en' = 'ru',
): string {
  const i = lang === 'en' ? 1 : 0;
  const byRace = race ? b.races.list[race as RaceKey]?.unitNames?.[unitKey] : undefined;
  if (byRace) return byRace[i];
  const def = b.units.list[unitKey] ?? b.units.npc[unitKey];
  if (!def) return unitKey;
  return lang === 'en' ? def.nameEn : def.nameRu;
}

/** Разведчики не воюют: только разведка и защита от чужих разведчиков. */
export function isScoutUnit(b: Balance, unitKey: string): boolean {
  return b.units.list[unitKey]?.class === 'scout';
}

/** Крестьяне: добыча в шахтах, перевозка и перевод; в атаку, разведку и подкрепление не ходят. */
export function isPeasantUnit(b: Balance, unitKey: string): boolean {
  return b.units.list[unitKey]?.class === 'peasant';
}

/** Делит отряд на разведчиков и боевые войска. */
export function splitScouts(
  b: Balance,
  units: Record<string, number>,
): { scouts: Record<string, number>; combat: Record<string, number> } {
  const scouts: Record<string, number> = {};
  const combat: Record<string, number> = {};
  for (const [k, n] of Object.entries(units)) {
    if (n <= 0) continue;
    if (isScoutUnit(b, k)) scouts[k] = n;
    else combat[k] = n;
  }
  return { scouts, combat };
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Дополняет сохранённый (в БД) раздел баланса ключами, появившимися в новой версии игры:
 * значения из БД сохраняются, недостающие ключи берутся из значений по умолчанию (рекурсивно).
 */
export function fillBalanceDefaults<T>(defaults: T, stored: unknown): T {
  if (!isPlainObject(defaults) || !isPlainObject(stored)) return (stored ?? defaults) as T;
  const res: Record<string, unknown> = { ...stored };
  for (const [k, v] of Object.entries(defaults)) {
    res[k] = k in stored ? fillBalanceDefaults(v, stored[k]) : v;
  }
  return res as T;
}

export type UnitStat = 'attack' | 'defense';

/** Стоимость улучшения атаки или защиты юнита до уровня level. */
export function unitUpgradeCost(b: Balance, unitKey: string, level: number): PartialResources {
  const u = b.units.list[unitKey];
  if (!u) return {};
  const cfg = b.units.upgrades;
  const mult = cfg.costMultiplier * level ** cfg.costExponent;
  const res: PartialResources = {};
  for (const [k, v] of Object.entries(u.cost) as [ResourceType, number][]) res[k] = Math.round(v * mult);
  return res;
}

/** Здание, в котором нанимается юнит (его уровень открывает юнит и ускоряет найм). */
export function unitBuildingOf(b: Balance, unitKey: string): BuildingType {
  const u = b.units.list[unitKey];
  return (u && b.units.classBuilding?.[u.class]) || 'barracks';
}

/** Требуемый уровень именно здания найма; элитные войска следуют его текущему пределу. */
export function unitRequiredBuildingLevel(b: Balance, unitKey: string): number {
  const unit = b.units.list[unitKey];
  if (!unit) throw new Error(`Неизвестный юнит ${unitKey}`);
  const building = unitBuildingOf(b, unitKey);
  return unit.requiresMaxBuildingLevel
    ? (b.buildings.list[building].maxLevel ?? b.buildings.maxLevel)
    : Math.max(1, unit.barracksLevel);
}

/** Стоимость исследования академии до уровня level. */
export function researchCost(b: Balance, code: string, level: number): PartialResources {
  const r = b.buildings.research.list[code];
  if (!r) return {};
  const mult = level ** b.buildings.research.costExponent;
  const res: PartialResources = {};
  for (const [k, v] of Object.entries(r.baseCost) as [ResourceType, number][]) res[k] = Math.round(v * mult);
  return res;
}

/** Время исследования (сек). */
export function researchTimeSec(b: Balance, level: number): number {
  const cfg = b.buildings.research;
  return Math.min(cfg.maxTimeSec, Math.round(cfg.baseTimeSec * level ** cfg.timeExponent));
}

/** Время улучшения войск (сек): 30 мин × уровень^1.3, не больше 12 ч. */
export function unitUpgradeTimeSec(b: Balance, level: number): number {
  const cfg = b.units.upgrades;
  return Math.min(cfg.maxTimeSec, Math.round(cfg.baseTimeSec * level ** cfg.timeExponent));
}

/** Предмет «Перемещение замка» в запасе игрока (user_boost_item): один переезд без оплаты золотом. */
export const RELOCATE_ITEM = 'relocate_castle';
/** Перелёт замка на остров без мостов (предмет в запасе). */
export const RELOCATE_ISLAND_ITEM = 'relocate_island';
/** Вид товара: пакет ресурсов лежит в запасе, пока игрок не активирует его в «Бустах». */
export const RESOURCE_PACK = 'resource_pack';
